import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { PageLayout } from '@/components/ui/page-layout';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { downloadService } from '../services';
import { ApkDownloadCard } from '../components';
import type { ApkDownloadInfoDto } from '../types';

export function DownloadsPage() {
  usePageTitle('Downloads');

  const [info, setInfo] = useState<ApkDownloadInfoDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInfo = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await downloadService.getDownloadInfo();
      setInfo(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch download info'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  return (
    <PageLayout
      title={
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Download className="h-5 w-5 text-info" />
          Downloads
        </h1>
      }
      description="Download the mobile application for Android devices."
    >
      {isLoading ? (
        <div className="flex flex-wrap gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <PanelSkeleton key={i} rows={2} className="w-72" />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{error}</p>
          <button
            onClick={fetchInfo}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-6">
          {info?.variants.map((v) => (
            <ApkDownloadCard key={v.variant} variant={v} />
          ))}
        </div>
      )}
    </PageLayout>
  );
}
